import {Component, Inject, Input, OnDestroy, OnInit} from "@angular/core";
import {TableControllerService} from "../../../services/table-controller.service";
import {FilterControllerService} from "../../../services/filter-controller.service";
//LZ import {FILTER_TYPE} from "../filter/custom-filter/filter.interface";
import {FILTER_TYPE} from "libComm";
import {GeneralService} from "../../../services/crud/general.service";
import {HttpParams} from "@angular/common/http";
import {Subject, takeUntil} from "rxjs";
import {ActionSet, Data} from "../../../models/layout";

@Component({
	selector: "form-page",
	templateUrl: "./form-page.component.html",
	styleUrls: ["./form-page.component.scss"],
	host: {'class': 'h-100'},
	providers: [TableControllerService, FilterControllerService]
})
export class FormPageComponent implements OnDestroy, OnInit
{
	private _interactionCycle: Subject<void> = new Subject<void>();

	@Input()
	apiParent: string;
	@Input()
	apiUrl: string;
	@Input()
	id: string;
	@Input()
	datasetData: Data[];
	@Input()
	actionSet?: ActionSet | null;

	// true while getting detail information
	isLoadingDetail = true;

	// True if a record exists for the given ID
	// Prevents the possibility of updating/inserting a record in the form if the details were not loaded properly
	isNew = false;

	// Determines whether an error panel should be displayed (hasError) and the message to show (message)
	errorManager: _Error = {hasError: false, message: null};

	// true if user have ACT_EDIT in actionSet
	hasUpdatePermission = false;
	// true if user have ACT_ADD in actionSet
	hasInsertPermission = false;

	// recuperare in init
	fields: _Field[] = [];

	// this field in defined as "any" because his type depends on the detail model
	detail: any = {};

	constructor(
		private generalDataService: GeneralService
	)
	{

	}

	ngOnDestroy(): void
	{
		this._interactionCycle.next();
		this._interactionCycle.complete();
	}

	/**
	 * builds the detail form asynchronously
	 */
	ngOnInit(): void
	{
		// 1. retrieve and set detail information, also determining when form is editable or not
		this.getDetail();

		// The action set is currently passed as a parameter to the component, inherited from the main page.  
		// However, if the action set is null, a retrieval mechanism from the database could be introduced in the future.  
		// ADD HERE FUTURE LOGIC FOR ACTIONSET IF NEEDED

		// 2. check the user permissions
		if (this.actionSet != null)
		{
			const actions = new Set(this.actionSet.actions.map(a => a.id));
			this.hasUpdatePermission = actions.has('ACT_EDIT');
			this.hasInsertPermission = actions.has('ACT_ADD');

			// ADD HERE FUTURE PERMISSION LOGIC
			// this.hasXPermission
			// this.hasYPermission
			// ...
		}

		// 3. set the fields to display in the form section based on the dataset_data table
		for (let data of this.datasetData)
		{
			this.fields.push(
				{
					id: this.snakeToCamel(data.columnName),
					label: data.columnLabel,
					type: data.type,
					option: [],
					editable: data.editable
				}
			)
		}

		// 4. retrieve combos values from backend api
		this.setCombos();
	}

	/**
	 * for each field with type 'DTY_LIST' invokes the combos api to retrieve the corresponding combo values
	 */
	setCombos()
	{
		let params = new HttpParams();
		for (let field of this.fields)
		{
			if (field.type == "DTY_LIST")
			{
				params = params.set("comboId", field.id);
				this.generalDataService.getData(this.apiParent + "/" + this.apiUrl + "/combos", null, params).pipe(
					takeUntil(this._interactionCycle)
				).subscribe({
					next: (res: any) =>
					{
						field.option = res.oContent;
					},
					error: (err) =>
					{
						console.error("Error fetching data:", err);
						this.isLoadingDetail = false;
					}
				});
			}
		}
	}

	/**
	 * retrive the detail information to display in the form
	 * save in 'detail:any' the dto retrieved from the BE, return {} if no record was founded
	 * set 'isPresent' equals to true if a record is founded
	 *
	 * NB: if isPresent == false in the params "isNew" will be set in the update request;
	 */
	getDetail()
	{
		let params = new HttpParams();
		if (this.id != null) {
			params = params.set("id", this.id);
		}
		this.generalDataService.getData(this.apiParent + "/" + this.apiUrl, null, params).pipe(
			takeUntil(this._interactionCycle)
		).subscribe({
			next: (res: any) =>
			{
				if (res.iResponseCode == 200)
				{
					// detail fetched
					this.resetError()
					this.detail = res.oContent ?? {};
					if (this.detail == null || this.detail.id == null)
					{
						this.isNew = true;
					}
				}
				else
				{
					// detail not found
					this.setError('RECORD_NOT_FOUND_FORM_ERROR');
					this.detail = {};
				}
				this.isLoadingDetail = false;
			},
			error: (err) =>
			{
				// error while fetching data
				this.setError('FETCH_FORM_ERROR');
				console.error("Error fetching data:", err);
				this.isLoadingDetail = false;
			}
		});
	}

	/**
	 * invoke the update api.
	 * The entire 'detail' object is sent to the backend, including fields that are not visible in the detail form
	 */
	updateDetail(): void
	{
		this.isLoadingDetail = true;

		let params = new HttpParams().set("isNew", this.isNew);

		this.generalDataService.postData(this.apiParent + "/" + this.apiUrl, [this.detail], params).pipe(
			takeUntil(this._interactionCycle)
		).subscribe({
			next: (res: any) =>
			{
				// record updated
				if (res.iResponseCode == 200)
				{
					this.resetError();
					this.detail = res.oContent;
					// If coming from an insert operation, update the ID
					if (this.id == null) {
						this.id = this.detail.id
					}
				} else {
					this.setError('UPDATE_FORM_ERROR');
				}
				this.isLoadingDetail = false;
			},
			error: (err) =>
			{
				// error while updating
				this.setError('UPDATE_FORM_ERROR');
				console.error("Error updating data:", err);
				this.isLoadingDetail = false;
			}
		});
	}

	protected readonly filterType = FILTER_TYPE;

	// convert snake case into camel
	private snakeToCamel(snake: string): string
	{
		return snake.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
	}

	/**
	 * Determines when a form field should be disabled.
	 * A field will be disabled if:
	 *  - the form is loading
	 *  - the user doesn't have edit permission (for existing record) ot insert permission (in case of new record)
	 */
	get isFormDisabled(): boolean {
		return this.isLoadingDetail || !this.hasCUPermission();
	}

	/**
	 * user can edit the form when
	 *   - has EDIT permission + the form contains data
	 *   - has INSERT permission + the form doesn't contain data ( -> we check for no data because otherwise user with INSERT permission can always
	 *   update data)
	 *
	 *   so, user has CU (Create-Update) permission
	 */
	hasCUPermission(): boolean {
		if (this.detail)
		{
			// if this.detail.id == null we are trying to insert a new record, we have to check the insert permission
			if (!this.detail.id)
			{
				return this.hasInsertPermission;
			}
			// if there is any data or id is not empty, check if user has edit permission
			return this.hasUpdatePermission;
		}
		return false;
	}

	// error handling methods
	/**
	 * reset the error message
	 * @private
	 */
	private resetError()
	{
		this.errorManager = {
			hasError: false,
			message: null
		}

	}

	/**
	 * set the error message
	 * @param errorMessage - message to display
	 * @private
	 */
	private setError(errorMessage: string)
	{
		this.errorManager = {
			hasError: true,
			message: errorMessage
		}
	}
}


export interface _Field
{
	id: string;
	label: string;
	type: string;
	option: any[];
	editable: boolean;
}

interface _DatasetData
{
	columnName: string;
	columnLabel: string;
	dataType: string;
	isEditable: boolean;
}

interface _Error
{
	hasError: boolean;
	message: string | null;
}
